//npm packages
import React, { useEffect, useCallback, memo, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import { Col, Row, Button, Collapse } from 'reactstrap';
import Swal from 'sweetalert2';
import classNames from 'classnames';
import dayjs from 'dayjs';

//redux actions
import {
  facilityForm,
  facilityFormReset,
  FACILITYFORM,
  countriesList
} from '../../redux/actions';

//elements starts with capital letter;
import { InputField, SelectField, FormBase, Validator } from '../../components';

//helper files
import strings from '../../globalization';
import utility from '../../utility';
import BasicRowColGrid from '../../utility/BasicRowColGrid';

const FacilityForm = memo(props => {
  const { params } = props.match;
  const dispatch = useDispatch();
  const {
    initialValues = {},
    comboType = {},
    countriesData,
    success,
    info,
    propLogo,
    isDark,
    licenseData
  } = useSelector(state => ({
    initialValues: state.facilityFormData.data.data,
    licenseData: state.licenseFormData.data.data,
    comboType: state.comboTypes,
    countriesData: state.countriesData.data,
    success: state.facilityFormData.data.success,
    info: state.facilityFormData.data.info,
    propLogo: state.logo,
    isDark: state.settingData.isDark
  }));

  const shouldGoBack = useMemo(() => {
    return (
      success &&
      (info === 'RecordSavedSuccessfully' ||
        info === 'RecordUpdatedSuccessfully')
    );
  }, [success, info]);

  const { _id: id } =
    (params.id.toLowerCase() === 'new' ? initialValues : licenseData) || {};
  if (shouldGoBack) {
    props.history.push({ pathname: `/licenseDetail/${id}` });
  }

  const [logo, setLogo] = useState(null);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    dispatch(
      facilityForm({
        action: 'load',
        _id: params.id,
        comboType: ['Region', 'Status', 'TimeZones', 'TrackingSystem']
      })
    );
    dispatch(
      countriesList({
        action: 'list',
        limit: 300
      })
    );

    return () => {
      dispatch(facilityFormReset());
    };
  }, []);

  useEffect(() => {
    props.initialize({
      ...initialValues,
      ExpirationDate:
        initialValues.ExpirationDate ||
        dayjs()
          .add(10, 'years')
          .endOf('month')
          .add(1, 'day')
    });
  }, [JSON.stringify(initialValues)]);

  const onSubmit = values => {
    const comboFields = [
      { name: 'Region', isArray: false },
      { name: 'IsActive', isArray: false },
      { name: 'CountryCode', isArray: false },
      { name: 'TimeZone', isArray: false },
      { name: 'TrackingSystem', isArray: false }
    ];
    const data = utility.processDataBeforeSave(values, comboFields);
    Object.assign(data, { tableName: 'Site' });
    if (logo) data.Logo = logo;
    dispatch(
      facilityForm({
        action: params.id.toLowerCase() === 'new' ? 'add' : 'update',
        ...data,
        GenerateCertificate: isOpen
      })
    );
  };

  const calculateImageSize = base64String => {
    let padding, inBytes, base64StringLength, kbytes;
    if (base64String.endsWith('==')) padding = 2;
    else if (base64String.endsWith('=')) padding = 1;
    else padding = 0;

    base64StringLength = base64String.length;
    inBytes = (base64StringLength / 4) * 3 - padding;
    kbytes = inBytes / 1000;
    return kbytes;
  };

  /**
   * @desc - convert the selected image into base 64 and assigned in state
   * @param {Event} event - file selection event
   */
  const onHospitalLogoSelect = useCallback((event, newValue) => {
    var reader = new FileReader();
    reader.onloadend = () => {
      let base64Value = reader.result;
      let validateHospitalLogo = calculateImageSize(base64Value);
      if (validateHospitalLogo > '50') {
        Swal.fire(strings.Warning, strings.LogoWarning, 'warning');
        return false;
      }
      setLogo(base64Value);
    };
    event.target.files.length > 0 &&
      reader.readAsDataURL(event.target.files[0]);
  }, []);

  const regionOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.Region
    ) || [];

  let timeZonesOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.TimeZones
    ) || [];
  timeZonesOptions = timeZonesOptions.map(timezone => ({
    ...timezone,
    value: timezone.label
  }));

  let countries =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'CountryCode' },
      countriesData.data
    ) || [];
  countries.sort((a, b) => a.label.localeCompare(b.label));
  countries = countries.map(country => ({ ...country, value: country.label }));

  const trackingSystemOptions =
    utility.remoteComboLoader(
      { valueProp: '_id', displayValue: 'DisplayValue' },
      comboType.TrackingSystem
    ) || [];
  return (
    <FormBase
      {...props}
      onSubmit={onSubmit}
      isDark={isDark}
      title={strings.SiteForm}
      subTitle={(initialValues && initialValues.Name) || strings.New}
      actionInfo={{ action: facilityForm, actionName: FACILITYFORM }}
      disableReset={true}
      saveText={strings.SaveContinue}
      historyDisable={params.id.toLowerCase() === 'new'}
    >
      <Row sm="12" md="12" lg="12">
        <BasicRowColGrid>
          {strings.AddressId}
          <Field
            name="AddressId"
            type="text"
            component={InputField}
            placeholder={strings.AddressId}
            validate={Validator.required}
            maxLength="100"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.SiteName}
          <Field
            name="Name"
            type="text"
            component={InputField}
            placeholder={strings.SiteName}
            validate={Validator.hasSpecialChar}
            maxLength="64"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.SiteAddress1}
          <Field
            name="SiteAddress1"
            type="text"
            component={InputField}
            placeholder={strings.SiteAddress1}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.SiteAddress2}
          <Field
            name="SiteAddress2"
            type="text"
            component={InputField}
            placeholder={strings.SiteAddress2}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.City}
          <Field
            name="City"
            type="text"
            component={InputField}
            placeholder={strings.City}
            validate={Validator.required}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.State}
          <Field
            name="State"
            type="text"
            component={InputField}
            placeholder={strings.State_Province_Region}
            validate={Validator.required}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.PostalCode}
          <Field
            name="PostalCode"
            type="text"
            component={InputField}
            placeholder={strings.PostalCode}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.TrackingSystem}
          <Field
            name="TrackingSystem"
            type="select"
            component={SelectField}
            options={trackingSystemOptions}
            isClearable={true}
            validate={Validator.required}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.CountryCode}
          <Field
            placeholder={strings.CountryCode}
            validate={Validator.required}
            name="CountryCode"
            type="select"
            component={SelectField}
            options={countries}
            isClearable={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.TimeZone}
          <Field
            placeholder={strings.TimeZone}
            validate={Validator.required}
            name="TimeZone"
            type="select"
            component={SelectField}
            options={timeZonesOptions}
            isClearable={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.Region}
          <Field
            name="Region"
            type="select"
            component={SelectField}
            options={regionOptions}
            isClearable={true}
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.Dealer}
          <Field
            name="Dealer"
            type="text"
            component={InputField}
            placeholder={strings.Dealer}
            maxLength="100"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.IDN}
          <Field
            name="IDN"
            type="text"
            component={InputField}
            placeholder={strings.IDN}
            maxLength="100"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.AccountNumber}
          <Field
            name="AccountNumber"
            type="text"
            component={InputField}
            placeholder={strings.AccountNumber}
            maxLength="100"
          />
        </BasicRowColGrid>
        <BasicRowColGrid>
          {strings.Logo}
          <Field
            type="file"
            accept="image/x-png,image/jpeg"
            component={InputField}
            onChange={onHospitalLogoSelect}
            name="Logo"
          />
        </BasicRowColGrid>
        <div className="row">
          <img
            decoding="auto"
            src={typeof propLogo === 'string' ? propLogo : logo}
            alt=""
            style={{ maxHeight: 88, marginLeft: 20 }}
          />
        </div>
      </Row>
      <Col sm="12" md="12" lg="12" className="mb-2">
        <Button
          onClick={() => setIsOpen(isOpen)}
          className={classNames('custom-header csr-text', {
            'custom-header-dark': isDark
          })}
          block
        >
          {` ${strings.GenerateCSR}`}
        </Button>
        <Collapse isOpen={isOpen}>
          {isOpen && (
            <Row className="mt-2">
              <BasicRowColGrid>
                {strings.FQDN}
                <Field
                  name="CommonName"
                  type="text"
                  component={InputField}
                  placeholder={strings.CommonName}
                  validate={Validator.minMaxValidator264}
                  maxLength={64}
                  minLength={2}
                />
              </BasicRowColGrid>
              <BasicRowColGrid>
                {strings.OrganizationalUnit}
                <Field
                  name="OrganizationalUnit"
                  type="text"
                  component={InputField}
                  placeholder={strings.Unit}
                  validate={Validator.minMaxValidator220}
                  maxLength={20}
                  minLength={2}
                />
              </BasicRowColGrid>
              <BasicRowColGrid>
                {strings.EmailAddress}
                <Field
                  name="EmailAddress"
                  type="text"
                  component={InputField}
                  placeholder={strings.EmailAddress}
                  validate={Validator.email}
                />
              </BasicRowColGrid>
              <BasicRowColGrid>
                {strings.ExpirationDate}
                <Field
                  name="ExpirationDate"
                  type="date"
                  component={InputField}
                  placeholder={strings.ExpirationDate}
                  min={
                    dayjs()
                      .add(1, 'day')
                      .toISOString()
                      .split('T')[0]
                  }
                />
              </BasicRowColGrid>
            </Row>
          )}
        </Collapse>
      </Col>
    </FormBase>
  );
});

const FacilityReduxForm = reduxForm({
  form: 'facilityForm',
  enableReinitialize: false,
  keepValues: false
})(FacilityForm);

export default FacilityReduxForm;
